import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbModal from '../security-toolbox-ui/StbModal';
import { useAssignLabPod } from '../../hooks/lab-hooks';
import { LabPool, LabPoolMember } from '../../support/labService';
import { labErrorMessage } from './LabShared';

export type LabPoolAssignModalProps = {
  pool: LabPool | null;
  /** Pre-selected when opened from a member row. */
  member?: LabPoolMember | null;
  onClose: () => void;
};

/**
 * Hand a pod to somebody else.
 *
 * Distinct from claiming, which always takes the assignee from the caller's token. Here an
 * email is sent, so the backend checks that the caller is the pool's contact or staff - a
 * check this form cannot do and does not pretend to. Only pods with room are offered, but
 * the pool can fill between the list loading and this submitting, which is why a 409 is a
 * normal answer rather than a bug.
 */
const LabPoolAssignModal: React.FC<LabPoolAssignModalProps> = ({ pool, member, onClose }) => {
  const [sessionKey, setSessionKey] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [teamLabel, setTeamLabel] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const assign = useAssignLabPod();

  useEffect(() => {
    if (!pool) return;

    setSessionKey(member?.session_key || '');
    setEmail('');
    setDisplayName('');
    setTeamLabel('');
    setFormError(null);
    assign.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool, member]);

  const candidates = (pool?.members || []).filter(
    (candidate) => candidate.available || candidate.session_key === member?.session_key,
  );

  const handleSubmit = async () => {
    if (!pool) return;

    if (!sessionKey) {
      setFormError('Choose a pod.');
      return;
    }

    if (!email.trim()) {
      setFormError('Who is it for?');
      return;
    }

    setFormError(null);

    try {
      await assign.mutateAsync({
        slug: pool.slug,
        session_key: sessionKey,
        email: email.trim(),
        ...(displayName.trim() ? { display_name: displayName.trim() } : {}),
        ...(teamLabel.trim() ? { team_label: teamLabel.trim() } : {}),
      });
      onClose();
    } catch {
      // Surfaced through assign.error.
    }
  };

  const submitError = formError || (assign.isError ? labErrorMessage(assign.error) : null);

  return (
    <StbModal
      open={!!pool}
      role="update"
      title={`Assign a pod in ${pool?.name || 'pool'}`}
      submitLabel="Assign"
      submitDisabled={assign.isPending}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? <p className="m-0 text-sm text-amber-800">{submitError}</p> : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">Pod</span>
          <select
            className="rounded border border-gray-300 p-2 text-sm"
            value={sessionKey}
            onChange={(evt) => setSessionKey(evt.target.value)}
          >
            <option value="">Choose a pod</option>
            {candidates.map((candidate) => (
              <option value={candidate.session_key} key={candidate.id}>
                {`${candidate.label || candidate.session_key} - ${candidate.assignment_count}/${candidate.max_assignees} assigned`}
              </option>
            ))}
          </select>
          {!candidates.length && (
            <span className="text-xs text-amber-700">
              Every pod in this pool is full. Stock the pool or release an assignment first.
            </span>
          )}
        </label>

        <TextField
          label="Assign to (email)"
          value={email}
          onChange={(evt) => setEmail(evt.target.value)}
          helperText="They see the pod under their own assignments."
          fullWidth
        />

        <TextField
          label="Display name (optional)"
          value={displayName}
          onChange={(evt) => setDisplayName(evt.target.value)}
          fullWidth
        />

        <TextField
          label="Team (optional)"
          value={teamLabel}
          onChange={(evt) => setTeamLabel(evt.target.value)}
          helperText="Groups assignments on the leaderboard and in reports."
          fullWidth
        />
      </div>
    </StbModal>
  );
};

export default LabPoolAssignModal;
